import React, { useImperativeHandle, useRef, useEffect, forwardRef, useState } from "react";
import { Modal } from 'bootstrap';
import { useSelector } from "react-redux";
import HttpAPICall from "../../../services/HttpAPICall";
import { toast } from "react-toastify";
import TapIcon from "../../../services/TapIcon";
import { HRM_API_BASE_URL_2 } from "../../../services/TapApiUrls";
import Loader from "../../../components/ui/Loader/Loader";
import DatePicker from "react-datepicker";
import moment from "moment";
import uuid from "react-uuid";
import TapSelect from "../../../components/ui/TapSelect";

const EmployeeStatusChange = forwardRef((props, ref) => {
    const access_token           =   useSelector(state => state.auth.access_token);
    const iamUserSite            =   useSelector(state => state.app.user_sites);

    const modalElem = useRef(null);
    const searchModal = useRef(null);
    const modalID = uuid();
    
    const [formData, setFormData] = useState({
        status                  :   "",
        status_since_date       :   "",
        remark                  :   "",
        site_id                 :   "", 
    });
    const [selectedEmployees, setSelectedEmployees]             = useState([]);
    const [isSubmitting, setIsSubmitting]                       = useState(false);
    const [allStatus, setAllStatus]                             = useState([]);
    const [filteredStatus, setFilteredStatus]                   = useState([]); 
    const [statusModalLoading, setStatusModalLoading]           = useState(false);
    const [commonCurrentStatus, setCommonCurrentStatus]         = useState("");
    const [iamUserSites, setIamUserSites]                       = useState([]); 
    const [commonSite, setCommonSite]                           = useState("");
    const [commonSiteId, setCommonSiteId]                       = useState(""); 

    useEffect(() => {    
        modalElem.current = document.getElementById(modalID);
        searchModal.current = new Modal(modalElem.current, {keyboard: false, backdrop: false});
        loadStatusOptions();
        if (iamUserSite && iamUserSite.length > 0) {
            const siteOptions = iamUserSite.map(site => ({
                value: site.id,
              label: `${site.site_name} (${site.site_code})`
            }));
            setIamUserSites(siteOptions);
        }
        
        return () => {
            if (searchModal.current) {
                searchModal.current.dispose();
            }
        };
    }, []);

    

    const loadStatusOptions = () => {
        setStatusModalLoading(true);
        HttpAPICall.withAthorization("GET", HRM_API_BASE_URL_2 + '/employee/add_form_data', access_token, { type: "status" }, {}, (resp) => {
            let { status } = resp.data;
            const statusOptions = status && status.length > 0
                ? status.map((s) => { return { value: s.key, label: s.name }; })
                : [];
            setAllStatus(statusOptions);
            setFilteredStatus(statusOptions);
        }).then(() => setStatusModalLoading(false));
    };

    useEffect(() => {
        if (allStatus.length > 0 && commonCurrentStatus) {
            const filtered = allStatus.filter(status => 
                status.label.toLowerCase() !== commonCurrentStatus.toLowerCase()
            );
            setFilteredStatus(filtered);
        } else {
            setFilteredStatus(allStatus);
        }
    }, [allStatus, commonCurrentStatus]);

    useImperativeHandle(ref, () => ({
        initModalHandlerEmployee: (data) => {
            if (data && data.isBulk) {
                setSelectedEmployees(data.employees || []);
                if (data.employees && data.employees.length > 0) {
                    setCommonCurrentStatus(data.employees[0].status_text || "");
                }
                setFormData({
                    status: "",
                    status_since_date: "",
                    remark: "",
                });
            } else {
                setSelectedEmployees(data ? [data] : []);
                setCommonCurrentStatus(data?.status_text || "");
                setFormData({
                    status: "",
                    status_since_date: "",
                    remark: "",
                });
            }
            openModalHandle();
        }
    }));

    const searchModalOpen = () => searchModal.current && searchModal.current.show();
    
    const searchModalClose = () => {
        searchModal.current && searchModal.current.hide();
        resetForm();
    };

    const openModalHandle = () => {
        searchModalOpen();       
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTapSelectChange = (selectedOption, fieldName) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: selectedOption ? selectedOption.value : ""
        }));
    };

    const handleDateChange = (date, fieldName) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : ""
        }));
    };

    const handleRemarkChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const payload = {
            ids: selectedEmployees.map(emp => emp.enc_id),
            status: formData.status,
            status_since_date: formData.status_since_date,
            remark: formData.remark || "",
            site_id: formData.site_id || "", 
        };

        HttpAPICall.withAthorization("PUT", HRM_API_BASE_URL_2 + '/multiple_employees_status_change', access_token, {}, payload, (response) => {
            toast.success(response.data.msg , {position: toast.POSITION.TOP_RIGHT });
            searchModalClose();
            if (props.onSuccess) {
                props.onSuccess();
            }
        }).catch(error => {
            toast.error(error?.response?.data?.message, {
                position: toast.POSITION.TOP_RIGHT
            });
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    const resetForm = () => {
        setFormData({
            status: "",
            status_since_date: "",
            remark: "",
            site_id: "",
        });
        setSelectedEmployees([]);
        setIsSubmitting(false);
        setCommonCurrentStatus("");
        setCommonSite(""); 
        setCommonSiteId("")
    };

    const renderEmployeeNames = () => {
        if (selectedEmployees.length === 0) return "-";
        const firstTwoNames = selectedEmployees.slice(0, 2).map(emp => {
            return emp.name ||
                emp.employee_name ||
                emp.full_name ||
                (emp.first_name && emp.last_name ? `${emp.first_name} ${emp.last_name}` : null) ||
                emp.email ||
                `Employee ${emp.id || emp.enc_id || ''}` ||
                'Unknown';
        }).filter(name => name).join(', ');

        const remainingCount = selectedEmployees.length - 2;

        if (remainingCount > 0) {
            return `${firstTwoNames} +${remainingCount} more`;
        }

        return firstTwoNames;
    };

    return (
        <div className="modal fade" id={modalID} tabIndex="-1" ref={modalElem}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Bulk Status Update
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            data-bs-dismiss="modal" 
                            onClick={searchModalClose} 
                            aria-label="Close"
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {statusModalLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    <div className="row mb-3">
                                        <label className="col-sm-3 col-form-label col-form-label-sm">
                                            Employee
                                        </label>
                                        <div className="col-sm-9">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm bg-light"
                                                value={renderEmployeeNames()}
                                                disabled
                                                
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="row mb-3">
                                        <label className="col-sm-3 col-form-label col-form-label-sm">
                                            Current Status
                                        </label>
                                        <div className="col-sm-9">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm bg-light"
                                                value={commonCurrentStatus}
                                                disabled
                                                
                                            />
                                        </div>
                                    </div>
                                        {commonCurrentStatus == "Inactive" &&
                                            <div className="row mb-3">
                                                <label className="col-sm-3 col-form-label col-form-label-sm require">
                                                    Site
                                                </label>
                                                <div className="col-sm-9">
                                                    <TapSelect
                                                        options={iamUserSites}
                                                        changeEvent={(selectedOption) => {
                                                            handleTapSelectChange(selectedOption, "site_id");
                                                        }}
                                                        isSearchable={true}
                                                        isClearable={true}
                                                        menuPlacement="bottom"
                                                        value={iamUserSites.find(
                                                            (s) => formData.site_id === s.value
                                                        )}
                                                        placeholder="Select Site"
                                                        containerHeight="30px"
                                                        fontSize="93%"
                                                        required={true}
                                                    />
                                                </div>
                                            </div>}
                                    <div className="row mb-3">
                                        <label className="col-sm-3 col-form-label col-form-label-sm require">
                                            Status
                                        </label>
                                        <div className="col-sm-9">
                                            <TapSelect
                                                options={filteredStatus}
                                                changeEvent={(selectedOption) => {
                                                    handleTapSelectChange(selectedOption, "status");
                                                }}
                                                isSearchable={true}
                                                isClearable={true}
                                                menuPlacement="bottom"
                                                value={filteredStatus.find(
                                                    (s) => formData.status === s.value
                                                )}
                                                placeholder="Select Status"
                                                containerHeight="30px"
                                                fontSize="93%"
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                    
                                    
                                    <div className="row mb-3">
                                        <label className="col-sm-3 col-form-label col-form-label-sm require">
                                            Status Date
                                        </label>
                                        <div className="col-sm-9">
                                            <DatePicker
                                                selected={formData.status_since_date ? moment(formData.status_since_date).toDate() : null}
                                                onChange={(date) => handleDateChange(date, "status_since_date")}
                                                dateFormat="dd-MMM-yyyy hh:mm a"
                                                className="form-control form-control-sm"
                                                showMonthDropdown
                                                showYearDropdown
                                                showTimeSelect
                                                timeIntervals={5}
                                                timeCaption="Time"
                                                autoComplete="off"
                                                scrollMonthYearDropdown
                                                required
                                                maxDate={new Date()}
                                                placeholderText="Please Enter Status Date"
                                            />
                                        </div>
                                    </div>
                                   
                                    <div className="row mb-3">
                                        <label className="col-sm-3 col-form-label col-form-label-sm">
                                            Remarks
                                        </label>
                                        <div className="col-sm-9">
                                            <textarea
                                                name="remark"
                                                rows="3"
                                                className="form-control form-control-sm"
                                                value={formData.remark}
                                                placeholder="Please Enter Remarks"
                                                onChange={handleRemarkChange}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={searchModalClose}
                                disabled={isSubmitting}
                            >
                                Close
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={isSubmitting || statusModalLoading}
                            > 
                                Save
                                {isSubmitting && 
                                    <TapIcon.FontAwesomeIcon 
                                        icon={TapIcon.faSyncAlt} 
                                        className="fa-spin ms-2" 
                                    />
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
});

export default EmployeeStatusChange;